import { useMemo, useState } from "react";
import { PublicTeamDTO } from "shared/services";
import {
	buildSidebarTaskSummaries,
	buildTaskHeaderStats,
	buildTaskItemViewModels,
	buildTaskStatusCounts,
	filterTaskItems,
	groupSidebarTasksByDay,
} from "../taskBoard.utils";
import { TaskBoardFilterStatus, TaskBoardState } from "../types";

interface UseTaskBoardViewInput {
	state: TaskBoardState;
	teams: PublicTeamDTO[];
}

export const useTaskBoardView = ({ state, teams }: UseTaskBoardViewInput) => {
	const [sidebarSearch, setSidebarSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<TaskBoardFilterStatus>("all");

	const sidebarTasks = useMemo(() => {
		return buildSidebarTaskSummaries(state, teams, sidebarSearch);
	}, [state, teams, sidebarSearch]);

	const sidebarGroups = useMemo(() => {
		return groupSidebarTasksByDay(sidebarTasks);
	}, [sidebarTasks]);

	const selectedTask = useMemo(() => {
		if (!state.selectedTaskId) return null;
		return state.taskById[state.selectedTaskId] ?? null;
	}, [state.selectedTaskId, state.taskById]);

	const taskItems = useMemo(() => {
		return buildTaskItemViewModels(state, selectedTask?.id ?? null, teams);
	}, [state, selectedTask?.id, teams]);

	const filteredTaskItems = useMemo(() => {
		return filterTaskItems(taskItems, statusFilter);
	}, [taskItems, statusFilter]);

	const statusCounts = useMemo(() => {
		return buildTaskStatusCounts(taskItems);
	}, [taskItems]);

	const headerStats = useMemo(() => {
		return buildTaskHeaderStats(taskItems);
	}, [taskItems]);

	const selectedTaskSummary = useMemo(() => {
		if (!selectedTask) return null;
		const current = sidebarTasks.find((task) => task.id === selectedTask.id);
		return current ?? null;
	}, [selectedTask, sidebarTasks]);

	return {
		sidebarSearch,
		setSidebarSearch,
		statusFilter,
		setStatusFilter,
		sidebarTasks,
		sidebarGroups,
		selectedTask,
		selectedTaskSummary,
		taskItems,
		filteredTaskItems,
		statusCounts,
		headerStats,
	};
};
